<script setup lang="ts">
import {
  type Field,
  type FieldSource,
  type ColumnConfig,
  type DisplayType,
  DISPLAY_TYPES,
  allowedDisplayTypes,
  SOURCE_META,
} from "../../../content/reports/ranked-explorer/fields";

const props = defineProps<{
  col: ColumnConfig;
  field: Field | undefined;
}>();

const emit = defineEmits<{
  (e: "update", patch: Partial<ColumnConfig>): void;
  (e: "remove"): void;
}>();

const isNum = computed(() => props.field?.kind === "number");
const allowed = computed(() => allowedDisplayTypes(props.field));
const sourceMeta = computed(() => SOURCE_META[(props.field?.source || "answer") as FieldSource]);

const editingLabel = ref(false);
const labelInput = ref(props.col.label);

function startEdit() {
  labelInput.value = props.col.label;
  editingLabel.value = true;
}

function commitLabel() {
  editingLabel.value = false;
  if (labelInput.value !== props.col.label) {
    emit("update", { label: labelInput.value });
  }
}
</script>

<template>
  <div class="border rounded-lg bg-card">
    <!-- Header row -->
    <div class="flex items-start gap-2 px-2.5 py-2">
      <!-- Source dot -->
      <span
        class="flex size-[22px] items-center justify-center rounded-md shrink-0 mt-0.5"
        :style="{
          background: `color-mix(in oklch, ${sourceMeta.color}, transparent 86%)`,
          color: sourceMeta.color,
        }"
      >
        <Icon mode="svg" size="13" :name="sourceMeta.icon" />
      </span>

      <!-- Label + ref -->
      <div class="flex-1 min-w-0">
        <div v-if="editingLabel" class="flex">
          <input
            v-model="labelInput"
            autofocus
            class="h-[26px] w-full border rounded-md px-2 text-[13px] font-semibold bg-background outline-none focus:ring-1 focus:ring-ring"
            @blur="commitLabel"
            @keydown.enter="commitLabel"
            @keydown.escape="editingLabel = false"
          />
        </div>
        <button
          v-else
          class="inline-flex items-center gap-1.5 max-w-full bg-transparent border border-transparent rounded-md px-1 py-0.5 -mx-1 -my-0.5 cursor-text hover:bg-accent group"
          @click="startEdit"
        >
          <span class="text-[13px] font-semibold truncate">
            {{ col.label || field?.label || col.fieldId }}
          </span>
          <Icon mode="svg" size="11" name="lucide:pencil" class="text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
        </button>
        <div class="font-mono text-[10.5px] text-muted-foreground truncate mt-0.5">
          {{ field?.ref || col.fieldId }}
        </div>
      </div>

      <!-- Remove -->
      <button
        class="size-[26px] inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer"
        title="Remove"
        @click="emit('remove')"
      >
        <Icon mode="svg" size="15" name="lucide:x" />
      </button>
    </div>

    <!-- Controls row -->
    <div class="flex items-center gap-2.5 px-2.5 pb-2.5 flex-wrap">
      <!-- Display type (number fields only) -->
      <template v-if="isNum">
        <Select
          :model-value="col.display || 'progress'"
          @update:model-value="(v: string) => emit('update', { display: v as DisplayType })"
        >
          <SelectTrigger class="h-7 w-auto min-w-[120px] rounded-md text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="dt in allowed" :key="dt" :value="dt">
              <div class="flex items-center gap-2">
                <span class="text-xs">{{ DISPLAY_TYPES[dt].label }}</span>
                <span class="text-[10px] text-muted-foreground">{{ DISPLAY_TYPES[dt].hint }}</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </template>
      <template v-else>
        <span class="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1.5 bg-muted rounded-md">
          <Icon mode="svg" size="13" name="lucide:type" />
          Text
        </span>
      </template>

      <!-- Alignment toggle -->
      <div class="inline-flex bg-muted rounded-md p-0.5 gap-0.5 ml-auto">
        <button
          class="h-7 px-2 inline-flex items-center rounded transition-colors cursor-pointer"
          :class="(col.align || 'left') === 'left'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'"
          title="Align left"
          @click="emit('update', { align: 'left' })"
        >
          <Icon mode="svg" size="14" name="lucide:align-left" />
        </button>
        <button
          class="h-7 px-2 inline-flex items-center rounded transition-colors cursor-pointer"
          :class="col.align === 'right'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'"
          title="Align right"
          @click="emit('update', { align: 'right' })"
        >
          <Icon mode="svg" size="14" name="lucide:align-right" />
        </button>
      </div>
    </div>
  </div>
</template>
